import React from 'react';
import { Button, ButtonGroup, Collapse, Typography } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { FormProvider } from 'react-hook-form';
import { FormSectionInfo } from '../../../form/components';
import { Form } from '../../../form';
import { RadioConvertUtils } from '../../../../utils/radio/radio';
import { RadioLabelled } from '../../../../packages/radio-labelled';
import { RHFRadioGroup } from '../../../../packages/react-hook-form/radio-group/radio-group';
import { ErrorMessage } from '../../../error-message';
import { PersonInput } from '../../../person-input';
import { BuyProcessFormUserTip } from '../user-tip/user-tip';
import { FormSectionContainer } from '../../../form/components/form-section-container/form-section-container';
import { PersonInputGroup } from '../person-input-group/person-input-group';
import { Stack } from '../../../stack/stack';
import { BuyProcessFormNextButton } from '../buy-process-form-next-button/buy-process-form-next-button';
import { BuyProcessFormAdminSubmitButton } from '../buy-process-form-admin-submit-button/buy-process-form-admin-submit-button';
import { RepresentativesBoardMemberFieldInput } from './components/board-member-field-input';
import {
  BoardMakeupErrors,
  chairmanObject,
  OfficialCeoLabel,
} from './representatives-utils';
import { RepresentativesSignAuthorityInput } from './components/sign-authority-input';
import { useRepresentatives } from './use-representatives';

const useStyles = makeStyles((theme) => ({
  insertField: {
    marginTop: theme.spacing(2),
  },
  collapsable: {
    marginTop: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(5),
  },
}));

export const BuyProcessFormRepresentatives = () => {
  const classes = useStyles();

  const {
    formMethods,
    representativesFieldArrayName,
    representativesFieldArray,
    onSubmit,
    people,
    onBack,
    onForward,
    isUpdating,
  } = useRepresentatives();

  const officialCeo = RadioConvertUtils.strToBool(
    formMethods.watch('officialCeo'),
  );

  const ceoProperty = (propName: string) => `ceo.${propName}`;

  return (
    <Form onSubmit={onSubmit}>
      <input ref={formMethods.register} name="boardMakeup" hidden />

      <BuyProcessFormUserTip>
        <div>
          <FormSectionInfo.InfoBoxTitled title="Bolagsföreträdare" />
          <Typography>
            Här anges de personer som ska företräda bolaget i olika roller såsom
            bolagets styrelse, övriga företrädare (t.ex. VD) samt bolagets
            firmateckning. Om du har frågor kring något så är du välkommen att
            ringa, maila eller chatta så hjälper vi dig gärna.
          </Typography>
        </div>
      </BuyProcessFormUserTip>

      <div>
        <FormSectionInfo.InfoBoxTitled title="Styrelse">
          <Typography>
            Minsta möjliga styrelse är 1 ledamot och 1 suppleant. Styrelsen
            skall bestå av lägst 1 och högst 5 styrelseledamöter med lägst 0 och
            högst 5 styrelsesuppleanter. Styrelseordföranden är den
            sammankallande i en styrelse och ska inte väljas om det är bara en
            ledamot. Är det fler än 1 ledamot måste man utse ordförande.
            <br />
            <br />
            <strong>Exempel:</strong> <br />
            1 Ledamot + 1 Suppleant <br />
            1 Ledamot + 1 Ledamot/Ordförande + 1 Suppleant <br />2 Ledamöter + 1
            Ledamot/Ordförande
          </Typography>
        </FormSectionInfo.InfoBoxTitled>

        <FormSectionContainer
          sectionTitle="Styrelse"
          className={classes.section}
        >
          <FormProvider {...formMethods}>
            <Stack gap={16}>
              {representativesFieldArray.fields.map((field, index) => (
                <PersonInputGroup key={field.id}>
                  <RepresentativesBoardMemberFieldInput
                    fieldArrayName={representativesFieldArrayName}
                    remove={representativesFieldArray.remove}
                    field={field}
                    index={index}
                    totalNumOfRepresentatives={
                      representativesFieldArray.fields.length
                    }
                  />
                </PersonInputGroup>
              ))}
            </Stack>
          </FormProvider>

          <Button
            variant="outlined"
            size="small"
            className={classes.insertField}
            onClick={() =>
              representativesFieldArray.insert(
                representativesFieldArray.fields.length,
                {},
              )}
          >
            Lägg till ytterligare en person
          </Button>
        </FormSectionContainer>
      </div>

      <div>
        <FormSectionInfo.InfoBoxTitled title="Verkställande Direktör (VD)">
          <Typography>
            En verkställande direktör är den person som ansvarar inför styrelsen
            för den löpande förvaltningen i ett aktiebolag. Det är inte
            obligatoriskt att utse en VD i de flesta fall, men undantag finns.
          </Typography>
        </FormSectionInfo.InfoBoxTitled>

        <FormSectionContainer className={classes.section}>
          <RHFRadioGroup
            label="Utse Verkställande Direktör? *"
            control={formMethods.control}
            name="officialCeo"
            defaultValue={RadioConvertUtils.false}
          >
            <RadioLabelled
              value={RadioConvertUtils.false}
              label={OfficialCeoLabel.false}
            />
            <RadioLabelled
              value={RadioConvertUtils.true}
              label={OfficialCeoLabel.true}
            />
          </RHFRadioGroup>

          <Collapse
            className={officialCeo ? classes.collapsable : ''}
            in={officialCeo}
          >
            <input
              hidden
              name={ceoProperty('relationId')}
              ref={formMethods.register}
            />
            <FormProvider {...formMethods}>
              <PersonInput
                people={people}
                fieldName="ceo"
                nameAugmentFunction={ceoProperty}
              />
            </FormProvider>
          </Collapse>
        </FormSectionContainer>
      </div>

      <div>
        <RepresentativesSignAuthorityInput
          errors={formMethods.errors}
          register={formMethods.register}
          control={formMethods.control}
          signAuthorityField="signAuthority"
        />
      </div>

      <ErrorMessage
        condition={!!formMethods.errors.boardMakeup}
        onClose={() => formMethods.clearErrors('boardMakeup')}
        action={(
          <React.Fragment>
            {formMethods.errors.boardMakeup?.message
              === BoardMakeupErrors.Chairman.mustHave && (
              <Button
                style={{ marginRight: '12px' }}
                color="inherit"
                size="small"
                onClick={() => {
                  representativesFieldArray.insert(
                    representativesFieldArray.fields.length,
                    chairmanObject,
                  );
                  window.scrollTo(0, 0);
                  formMethods.clearErrors('boardMakeup');
                }}
              >
                Lägg till en ordförande
              </Button>
            )}
          </React.Fragment>
        )}
      >
        {formMethods.errors.boardMakeup?.message}
      </ErrorMessage>

      <ButtonGroup>
        <Button onClick={onBack}>Tillbaka</Button>
        <BuyProcessFormNextButton onClick={onForward}>
          Nästa
        </BuyProcessFormNextButton>
        <BuyProcessFormAdminSubmitButton loading={isUpdating}>
          Spara
        </BuyProcessFormAdminSubmitButton>
      </ButtonGroup>
    </Form>
  );
};
